import { useState } from 'react';
import { motion } from 'framer-motion';

const BecomeAgent = () => {
  const [formData, setFormData] = useState<any>({});
  const [charCounts, setCharCounts] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT Abuja', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];

  const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};


  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Earnings',
      description: 'Earn attractive commissions on every successful registration and service delivery'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Schedule',
      description: 'Work at your own pace and manage your time according to your availability'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Support',
      description: 'Receive comprehensive training and ongoing support from our experienced team'
    },
    {
      icon: 'ri-team-line',
      title: 'Growing Network',
      description: 'Join a community of successful agents across Nigeria and expand your professional network'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Performance Bonuses',
      description: 'Unlock additional rewards and incentives based on your performance and achievements'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Official Recognition',
      description: 'Get certified as an authorized agent with official credentials and marketing materials'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    
    if (e.target instanceof HTMLTextAreaElement && e.target.maxLength) {
      setCharCounts((prev: any) => ({ ...prev, [name]: value.length }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSubmit = new FormData(form);
      const urlEncodedData = new URLSearchParams(formDataToSubmit as any).toString();

      const response = await fetch('https://readdy.ai/api/form/d5193to9ei8qjpb9fqp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({});
        setCharCounts({});
        setTimeout(() => {
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.section
        id="agent"
        className="py-20 bg-gradient-to-br from-orange-50 to-white"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        >

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUp} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Become an Agent
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Join our network of agents and help us deliver exceptional services across Nigeria
            </p>
        </motion.div>


        {/* Benefits Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Why Join Us?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                    key={index}
                    variants={fadeUp}
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow"
                >
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg mb-4">
                  <i className={`${benefit.icon} text-2xl text-orange-600`}></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm font-medium">
                  ✓ Application submitted successfully! We'll review your application and get back to you soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm font-medium">
                  ✗ Submission failed. Please try again.
                </p>
              </div>
            )}

            {/* Agent Application Form */}
            <motion.div variants={fadeUp} className="max-h-[600px] overflow-y-auto pr-2">
              <form
                id="agent-application-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <h3 className='text-center font-bold text-black-300'>Become an Agent</h3>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address *</label>
                  <input
                    type="text"
                    name="residentialAddress"
                    value={formData.residentialAddress || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local Government Area (LGA) *</label>
                  <input
                    type="text"
                    name="lga"
                    value={formData.lga || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  <select
                    name="state"
                    value={formData.state || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select State</option>
                    {nigerianStates.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Becoming an Agent *</label>
                  <textarea
                    name="reason"
                    value={formData.reason || ''}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  />
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {charCounts.reason || 0}/500 characters
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience in Similar Roles *</label>
                  <select
                    name="experience"
                    value={formData.experience || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select Experience</option>
                    <option value="0-1">0–1 years</option>
                    <option value="2-5">2–5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Previous Similar Registration or Agent Roles *</label>
                  <textarea
                    name="previousRoles"
                    value={formData.previousRoles || ''}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  />
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {charCounts.previousRoles || 0}/500 characters
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Service Area or Territory</label>
                  <input
                    type="text"
                    name="preferredArea"
                    value={formData.preferredArea || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:bg-gray-400"
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default BecomeAgent;
